import * as yup from 'yup';
import {
    isWhatsappNotificationMissingCustomerPhone,
    WHATSAPP_MISSING_CUSTOMER_PHONE_MESSAGE,
    WHATSAPP_NOTIFY_BY_VALUE
} from './utils/whatsappNotifications';

export interface OrderFormValues {
    buyAndGetProducts: any[]; // replace "any" with a more specific type if available
    customerInfo: any | null;
    delivery: boolean;
    pickup: boolean;
    express: boolean;
    discountCode: string;
    _id: string;
    sequence: string;
    folioPrefix?: string | null;
    orderType?: 'standard' | 'return';
    returnSourceOrderId?: string;
    returnItemsChargeMode?: 'no_charge' | 'charge';
    deliveryInfo: {
        date: Date;
        fromTime: string;
        toTime: string;
    };
    pickupInfo: any | null;
    phone: string;
    email: string;
    address: any | null;
    notes: string;
    privateNotes: string;
    taxesType: string;
    products: any[];
    creditApplied: number;
    amountPaid: number;
    customer: string;
    pickingUpDateTime: Date | null;
    cleanedDateTime: Date | null;
    readyDateTime: Date | null;
    deliveringDateTime: Date | null;
    collectedDateTime: Date | null;
    cancelledDateTime: Date | null;
    paidDateTime: Date | null;
    status: string;
    store: string;
    paymentMethod: string;
    prepaidAmount: number;
    prepaidPaymentMethod: string;
    notifyBy: string;
    deliveryDateFormatted: string;
    subtotal: number;
    total: number;
    customerName: string;
    customerDiscount: number;
    productTotal: number;
    taxesTotal: number;
    productTotalWithoutDiscount: number;
    totalDiscountAmount: number;
    shippingServiceTotal: number;
    discountsToApply: any[];
    usePrepaid: boolean;
    paymentLines: any[];
    cashierBox: string;
    discountCodeObj: any | null;
    totalQuantity: number;
    loyaltyPointsEarned: number | null;
    loyaltyPointsExpectedToEarn: number | null;
    loyaltyPointsRedeemed: number | null;
    loyaltyDiscountAmount: number | null;
}

const schema = yup.object({
    // Root-level fields
    buyAndGetProducts: yup.array(),
    customerInfo: yup.mixed().nullable(),
    express: yup.boolean(),
    discountCode: yup.string(),
    _id: yup.string(),
    sequence: yup.string(),
    folioPrefix: yup.string().nullable().default(''),
    orderType: yup.mixed<'standard' | 'return'>().oneOf(['standard', 'return']).optional(),
    returnSourceOrderId: yup.string().optional(),
    returnItemsChargeMode: yup
        .mixed<'no_charge' | 'charge'>()
        .oneOf(['no_charge', 'charge'])
        .optional(),
    delivery: yup.boolean().required("Requiere entrega es requerido"),
    // Conditional validation for deliveryInfo based on the "delivery" flag:
    // Define deliveryInfo as an object with basic fields...
    deliveryInfo: yup.object({
        date: yup.date().required("Fecha de entrega es requerido"),
        fromTime: yup.string(),
        toTime: yup.string()
    }).when("delivery", ([delivery], schema) => {
        // If delivery is true, make fromTime and toTime required.
        if (delivery) {
            return schema.shape({
                fromTime: yup.string().required("Hora de entrega desde es requerido"),
                toTime: yup.string().required("Hora de entrega hasta es requerido")
            });
        }
        return schema;
    }),
    pickup: yup.boolean().required("Requiere recolección es requerido"),
    pickupInfo: yup.object({
        date: yup.date().nullable(),
        fromTime: yup.string().nullable(),
        toTime: yup.string().nullable()
    }).when("pickup", ([pickup], schema) => {
        // If pickup is true, make object required.
        if (pickup) {
            return schema.shape({
                date: yup.date().required("Fecha de recolección es requerido"),
                fromTime: yup.string().required("Hora de recolección desde es requerido"),
                toTime: yup.string().required("Hora de recolección hasta es requerido")
            });
        }
        return schema;
    }).nullable(),
    phone: yup.string().nullable(),
    email: yup.string().nullable(),
    address: yup.mixed().nullable(),
    notes: yup.string().nullable(),
    privateNotes: yup.string().nullable(),
    taxesType: yup.string(),
    products: yup.array(),
    creditApplied: yup.number(),
    amountPaid: yup.number(),
    customer: yup.string().required("Cliente es requerido"),
    pickingUpDateTime: yup.date().nullable(),
    cleanedDateTime: yup.date().nullable(),
    readyDateTime: yup.date().nullable(),
    deliveringDateTime: yup.date().nullable(),
    collectedDateTime: yup.date().nullable(),
    cancelledDateTime: yup.date().nullable(),
    paidDateTime: yup.date().nullable(),
    status: yup.string(),
    store: yup.string().required("Sucursal es requerido"),
    paymentMethod: yup.string().required("Método de pago es requerido"),
    prepaidAmount: yup.number(),
    prepaidPaymentMethod: yup.string(),
    notifyBy: yup.string().test(
        'whatsapp-customer-phone-required',
        WHATSAPP_MISSING_CUSTOMER_PHONE_MESSAGE,
        function (value) {
            if (value !== WHATSAPP_NOTIFY_BY_VALUE) {
                return true;
            }

            return !isWhatsappNotificationMissingCustomerPhone({
                ...this.parent,
                notifyBy: value
            }, this.options.context || {});
        }
    ),
    deliveryDateFormatted: yup.string(),
    subtotal: yup.number(),
    total: yup.number(),
    customerName: yup.string(),
    customerDiscount: yup.number(),
    productTotal: yup.number(),
    taxesTotal: yup.number(),
    productTotalWithoutDiscount: yup.number(),
    totalDiscountAmount: yup.number(),
    shippingServiceTotal: yup.number(),
    discountsToApply: yup.array(),
    usePrepaid: yup.boolean(),
    paymentLines: yup.array(),
    cashierBox: yup.string().when("_id", ([_id], schema) => {
        if (!_id) {
            // when creating a new order, cashierBox is required
            return schema.required("Caja registradora es requerido");
        }
        return schema;
    }),
    discountCodeObj: yup.mixed().nullable(),
    totalQuantity: yup.number(),
    loyaltyPointsEarned: yup.number().nullable(),
    loyaltyPointsExpectedToEarn: yup.number().nullable(),
    loyaltyPointsRedeemed: yup.number().nullable(),
    loyaltyDiscountAmount: yup.number().nullable(),
});

export default schema;
